import { useCallback, useEffect, useState } from 'react'
import * as React from 'react'
import Event from '../../Event'

import {
  SearchableDropdown,
  SelectOption,
} from '@paytrace/pt-skel/Component'
import PubSub from '@paytrace/pt-skel/Event/PubSub'

export interface Customer {
  customer_label: string;
  name: string;
  customer_id: string;
  id: number;
  cid: number;
}

interface SelectOpt {
  name: string;
  val: string;
}

export interface CustomerSelectProps {
  searchObject: {
    limit: number;
    offset: number;
    merchantId: string | number;
    name_filter: string;
    search_term?: string;
  };
  onCustomerSelect: (customer: Customer | null) => void;
  initialCustomerId?: string | null;
  disabled?: boolean;
  label?: string | null;
}

const defaultCustomerList: SelectOpt[] = [
  { name: '-- Customer ID / Name --', val: '' },
]

const self = Symbol('CustomerSelect')

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(() => {
      func(...args)
    }, wait)
  }
}

function customersToOpts (customers: Customer[] | null): SelectOpt[] {
  if (!customers) return []

  const customerMap = customers.map((customer) => ({
    name: `${customer.customer_label || customer.customer_id} / ${
      customer.name || 'N/A'
    }`,
    val: String(customer.customer_id),
  }))

  return customerMap
}

export default function RecurringCustomerSelect ({
  searchObject,
  onCustomerSelect,
  initialCustomerId,
  disabled = false,
  label = '',
}: CustomerSelectProps): React.ReactElement {
  const [allDropdownOptions, setAllDropdownOptions] =
    useState<SelectOpt[]>(defaultCustomerList)
  const [dropdownOptions, setDropdownOptions] =
    useState<SelectOpt[]>(defaultCustomerList)
  const [selectedDropdownOption, setSelectedDropdownOption] = useState<
    SelectOption | undefined
  >(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>('')

  useEffect(() => {
    if (initialCustomerId) {
      PubSub.pub(Event.GET_CUSTOMER_REQUEST, { customerId: initialCustomerId })
    } else {
      setSelectedDropdownOption(undefined)
    }
  }, [initialCustomerId])

  useEffect(() => {
    setIsLoading(true)
    const handleCustomerResponse = (data: { customer: Customer } | null) => {
      const customer = data?.customer ?? null
      if (customer) {
        const customerOpt = customersToOpts([customer])[0]
        setDropdownOptions((prevOpts) => {
          const exists = prevOpts.some((opt) => opt.val === customerOpt.val)
          return exists
            ? prevOpts
            : [defaultCustomerList[0], customerOpt, ...prevOpts.slice(1)]
        })
        setAllDropdownOptions((prevOpts) => {
          const exists = prevOpts.some((opt) => opt.val === customerOpt.val)
          return exists
            ? prevOpts
            : [defaultCustomerList[0], customerOpt, ...prevOpts.slice(1)]
        })
        setSelectedDropdownOption(customerOpt)
        onCustomerSelect(customer)
      } else {
        onCustomerSelect(null)
        setSelectedDropdownOption(undefined)
      }
    }

    const handleListResponse = (customers: Customer[] | null) => {
      setIsLoading(false)
      const customerOpts = customersToOpts(customers)
      setDropdownOptions([defaultCustomerList[0], ...customerOpts])
      setAllDropdownOptions([defaultCustomerList[0], ...customerOpts])
    }

    PubSub.sub(self, Event.GET_CUSTOMER_RESPONSE, handleCustomerResponse)
    PubSub.sub(self, Event.GET_CUSTOMER_LIST_RESPONSE, handleListResponse)

    PubSub.pub(Event.GET_CUSTOMER_LIST_REQUEST, {
      ...searchObject,
      search_term: '',
    })

    setIsLoading(false)
    return () => {
      PubSub.unsub(Event.GET_CUSTOMER_RESPONSE)
      PubSub.unsub(Event.GET_CUSTOMER_LIST_RESPONSE)
    }
  }, [defaultCustomerList, onCustomerSelect, searchObject])

  const handleSelectionChange = useCallback(
    (selection: SelectOption) => {
      setSelectedDropdownOption(selection)
      if (selection && selection.val) {
        setIsLoading(true)
        PubSub.pub(Event.GET_CUSTOMER_REQUEST, { customerId: selection.val })
      } else {
        onCustomerSelect(null)
      }
    },
    [currentSearchTerm, onCustomerSelect]
  )

  const debouncedSearch = useCallback(
    debounce((term: string) => {
      if (!searchObject.merchantId) return

      setIsLoading(true)
      const updatedSearchObject = { ...searchObject, search_term: term }
      PubSub.pub(Event.GET_CUSTOMER_LIST_REQUEST, updatedSearchObject)
    }, 500),
    [searchObject]
  )

  const handleSearchInputChange = (term: string) => {
    setCurrentSearchTerm(term)
    if (term.trim() === '') {
      setIsLoading(false)
      setDropdownOptions(allDropdownOptions)
      setSelectedDropdownOption(undefined)
      return
    }

    const lowerCaseTerm = term.toLowerCase().trim()
    const matchingCustomers = dropdownOptions.filter((customer) => customer.name.toLowerCase().includes(lowerCaseTerm))
    if (matchingCustomers.length > 0) {
      setDropdownOptions(matchingCustomers)
    } else {
      debouncedSearch(term)
    }
  }

  return (
      <SearchableDropdown
        options={dropdownOptions}
        handleSearchChange={handleSearchInputChange}
        handleSelectionChange={handleSelectionChange}
        selectedValue={selectedDropdownOption}
        label={label ?? ''}
        placeHolder="-- Customer ID / Name --"
      />
  )
}
